import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import ReviewList from "../components/ReviewList";
import VerificationDashboard from "../components/VerificationDashboard";
import { Link } from "react-router-dom";

export default function ProfilePage() {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);

  useEffect(() => {
    if (user) {
      // Always fetch from profiles table for latest avatar_url
      supabase.from('profiles').select('avatar_url, name').eq('id', user.id).single().then(({ data }) => {
        if (data) {
          setAvatarUrl(data.avatar_url || "");
          if (data.name) setName(data.name);
        }
      });
      setEmail(user.email || "");
    }
  }, [user]);

  async function handleAvatarUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}.${fileExt}`;
      const { data, error: uploadError } = await supabase.storage.from('profile-avatars').upload(fileName, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: publicUrlData } = supabase.storage.from('profile-avatars').getPublicUrl(fileName);
      setAvatarUrl(publicUrlData.publicUrl);
      // Update profiles table immediately
      await supabase.from('profiles').update({ avatar_url: publicUrlData.publicUrl }).eq('id', user.id);
      setSuccess("Avatar uploaded!");
    } catch (err) {
      setError("Failed to upload avatar");
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const { error: updateError } = await supabase.from('profiles').update({ name, avatar_url: avatarUrl }).eq('id', user.id);
      if (updateError) throw updateError;
      setSuccess("Profile updated!");
    } catch (err) {
      setError("Failed to update profile");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container-center mx-auto max-w-lg py-8">
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl border border-white/40 shadow-xl p-8">
        <h1 className="text-2xl font-bold mb-6 text-center">My Profile</h1>
        <form onSubmit={handleSave} className="space-y-6">
          <div className="flex flex-col items-center gap-3 mb-4">
            <div className="w-28 h-28 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center border-4 border-white shadow">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-4xl text-slate-400">👤</span>
              )}
            </div>
            <label className="btn-secondary cursor-pointer px-4 py-2 text-sm">
              Change Photo
              <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
            </label>
          </div>
          <div>
            <label className="form-label">Display Name</label>
            <input
              className="form-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your name"
              required
            />
          </div>
          <div>
            <label className="form-label">Email</label>
            <input
              className="form-input bg-slate-100 cursor-not-allowed"
              value={email}
              disabled
            />
          </div>
          {error && <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700">{error}</div>}
          {success && <div className="p-2 bg-green-50 border border-green-200 rounded text-green-700">{success}</div>}
          <button type="submit" className="btn-primary w-full text-lg py-3" disabled={loading}>
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </form>
              <hr className="my-8" />
              
              {/* History Links */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <Link
                  to="/purchase-history"
                  className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Purchase History</h3>
                      <p className="text-sm text-slate-600">Items you've bought</p>
                    </div>
                  </div>
                </Link>

                <Link
                  to="/sales-history"
                  className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200 hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 5v5m0-5c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Sales History</h3>
                      <p className="text-sm text-slate-600">Items you've sold</p>
                    </div>
                  </div>
                </Link>
              </div>

              <hr className="my-8" />
              
              {/* Verification Section - Hidden until we have enough users */}
              {/* <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4">Account Verification</h2>
                <VerificationDashboard showTitle={false} />
              </div>

              <hr className="my-8" /> */}
              
              <h2 className="text-xl font-semibold mb-4">Reviews & Ratings</h2>
              <ReviewList userId={user.id} />
      </div>
    </div>
  );
}
